/**
 * Image Cache Service
 * 
 * Provides client-side caching for images to prevent unnecessary re-downloads
 * when filters change or components re-render.
 */

// In-memory cache for preloaded images
const imageCache = new Map<string, HTMLImageElement>();
const loadingPromises = new Map<string, Promise<HTMLImageElement>>();

/**
 * Preload an image and cache it
 * @param url The image URL to preload
 * @returns Promise that resolves when the image is loaded
 */
export function preloadImage(url: string): Promise<HTMLImageElement> {
  if (!url) return Promise.resolve(new Image());
  
  // Return cached image if available
  if (imageCache.has(url)) {
    return Promise.resolve(imageCache.get(url)!);
  }
  
  // Return existing loading promise if image is currently loading
  if (loadingPromises.has(url)) {
    return loadingPromises.get(url)!;
  }
  
  // Create new loading promise
  const loadPromise = new Promise<HTMLImageElement>((resolve) => {
    const img = new Image();
    
    img.onload = () => {
      imageCache.set(url, img);
      loadingPromises.delete(url);
      resolve(img);
    };
    
    img.onerror = () => {
      loadingPromises.delete(url);
      resolve(img); // Resolve even on error to not block
    };
    
    img.src = url;
  });
  
  loadingPromises.set(url, loadPromise);
  return loadPromise;
}

/**
 * Preload multiple images in parallel
 * @param urls Array of image URLs to preload
 * @returns Promise that resolves when all images are loaded
 */
export function preloadImages(urls: string[]): Promise<HTMLImageElement[]> {
  return Promise.all(urls.filter(Boolean).map(preloadImage));
}

/**
 * Check if an image is already cached
 * @param url The image URL to check
 * @returns True if the image is cached
 */
export function isImageCached(url: string): boolean {
  return imageCache.has(url);
}

/**
 * Get the number of cached images
 * @returns The cache size
 */
export function getCacheSize(): number {
  return imageCache.size;
}

/**
 * Clear the image cache (useful for memory management)
 * @param olderThan Optional: only clear images not accessed recently
 */
export function clearCache(): void {
  imageCache.clear();
  loadingPromises.clear();
}

/**
 * Generate a cache-friendly URL with version parameter
 * This ensures the browser caches the image but refreshes when the record is updated
 * @param baseUrl The base image URL
 * @param updatedAt The record's updated timestamp (ISO string or Date)
 * @returns URL with cache version parameter
 */
export function getCacheableUrl(baseUrl: string, updatedAt?: string | Date): string {
  if (!baseUrl) return '';
  
  // If URL already has query params, append with &, otherwise use ?
  const separator = baseUrl.includes('?') ? '&' : '?';
  
  // Use the updated timestamp as a cache key
  // This ensures the browser caches the image but fetches a new one when the record changes
  if (updatedAt) {
    const timestamp = typeof updatedAt === 'string' 
      ? new Date(updatedAt).getTime() 
      : updatedAt.getTime();
    return `${baseUrl}${separator}v=${timestamp}`;
  }
  
  return baseUrl;
}

/**
 * Create a stable image URL that won't change between renders
 * unless the actual image data changes
 * @param record The record containing the image
 * @param imageField The field name containing the image filename
 * @param collectionName The collection name
 * @returns A stable, cacheable URL
 */
export function getStableImageUrl(
  record: { id: string; updated?: string; [key: string]: any },
  imageField: string,
  collectionName: string,
  baseUrl: string
): string {
  const filename = record[imageField];
  if (!filename) return '';
  
  const url = `${baseUrl}/api/files/${collectionName}/${record.id}/${filename}`;
  return getCacheableUrl(url, record.updated);
}
